import DeleteIcon from "@mui/icons-material/Delete";
import {
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	InputLabel,
	ListItemIcon,
	ListItemText,
	MenuItem,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { type Assignment, getAssignmentById } from "../../../api/assignments";
import { deleteVote, type PersonVote, updateVote } from "../../../api/votes";
import { formatDateTime } from "../../../utils/date-formatting";
import { getActionsForAssignment } from "../../grading/action-buttons";

interface VoteEditDialogProps {
	open: boolean;
	onClose: () => void;
	vote: PersonVote | null;
	onUpdate?: () => void;
}

export const VoteEditDialog = ({
	open,
	onClose,
	vote,
	onUpdate,
}: VoteEditDialogProps) => {
	const [grade, setGrade] = useState("");
	const [saving, setSaving] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [assignment, setAssignment] = useState<Assignment | null>(null);
	const [loadingAssignment, setLoadingAssignment] = useState(false);

	// Fetch assignment to check egenkonsult status
	useEffect(() => {
		if (vote?.assignment && open) {
			setLoadingAssignment(true);
			getAssignmentById(vote.assignment)
				.then((a) => setAssignment(a))
				.catch(() => setAssignment(null))
				.finally(() => setLoadingAssignment(false));
		}
	}, [vote?.assignment, open]);

	useEffect(() => {
		if (vote) {
			setGrade(vote.grade || "");
			setError(null);
			setConfirmDelete(false);
		}
	}, [vote]);

	// Get valid grade options based on assignment type (same as grading interface)
	const gradeOptions = useMemo(
		() => getActionsForAssignment(assignment?.egenkonsult ?? false),
		[assignment?.egenkonsult]
	);

	const handleSave = async () => {
		if (!vote || grade === vote.grade) {
			onClose();
			return;
		}
		setSaving(true);
		setError(null);
		try {
			await updateVote(vote.id, { grade });
			onUpdate?.();
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to save");
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (!vote) return;
		if (!confirmDelete) {
			setConfirmDelete(true);
			return;
		}
		setDeleting(true);
		setError(null);
		try {
			await deleteVote(vote.id);
			onUpdate?.();
			onClose();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Failed to delete");
		} finally {
			setDeleting(false);
		}
	};

	const handleClose = () => {
		setConfirmDelete(false);
		onClose();
	};

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="xs"
			fullWidth
			data-component="vote-edit-dialog"
		>
			<DialogTitle>Edit Vote</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					{/* Vote info */}
					<Box>
						<Typography variant="caption" color="text.secondary">
							Assignment
						</Typography>
						<Typography variant="body2">
							{vote?.assignment_name || "—"}
						</Typography>
					</Box>
					<Box>
						<Typography variant="caption" color="text.secondary">
							Voted by
						</Typography>
						<Typography variant="body2">
							{vote?.user_name || "—"} on{" "}
							{vote?.created ? formatDateTime(vote.created) : "—"}
						</Typography>
					</Box>

					{/* Grade selector */}
					{loadingAssignment ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
							<CircularProgress size={24} />
						</Box>
					) : (
						<FormControl fullWidth size="small">
							<InputLabel id="vote-grade-label">Grade</InputLabel>
							<Select
								labelId="vote-grade-label"
								value={grade}
								label="Grade"
								onChange={(e) => setGrade(e.target.value)}
								data-component="vote-grade-select"
								renderValue={(selected) => {
									const opt = gradeOptions.find(
										(o) => o.action === selected
									);
									return opt ? opt.name : selected;
								}}
							>
								{gradeOptions.map((opt) => (
									<MenuItem key={opt.action} value={opt.action}>
										<ListItemIcon sx={{ minWidth: 36 }}>
											{opt.icon}
										</ListItemIcon>
										<ListItemText>{opt.name}</ListItemText>
									</MenuItem>
								))}
								{/* If current grade isn't in the list, show it anyway */}
								{grade &&
									!gradeOptions.find((o) => o.action === grade) && (
										<MenuItem value={grade}>
											<ListItemText>{grade}</ListItemText>
										</MenuItem>
									)}
							</Select>
						</FormControl>
					)}

					{error && (
						<Typography color="error" variant="body2">
							{error}
						</Typography>
					)}

					{/* Delete confirmation */}
					{confirmDelete && (
						<Typography
							color="error"
							variant="body2"
							sx={{ fontWeight: 500 }}
						>
							Click delete again to confirm removal of this vote.
						</Typography>
					)}
				</Stack>
			</DialogContent>
			<DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
				<Button
					color="error"
					onClick={handleDelete}
					disabled={saving || deleting}
					startIcon={<DeleteIcon />}
					variant={confirmDelete ? "contained" : "text"}
					data-component="delete-vote-button"
				>
					{deleting ? "Deleting..." : "Delete"}
				</Button>
				<Stack direction="row" spacing={1}>
					<Button onClick={handleClose} disabled={saving || deleting}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disabled={saving || deleting || grade === vote?.grade}
						data-component="save-vote-button"
					>
						{saving ? "Saving..." : "Save"}
					</Button>
				</Stack>
			</DialogActions>
		</Dialog>
	);
};
